import { Mail, MapPin, UserCheck } from 'lucide-react';

import { ScrollReveal, SplitReveal } from '@/components/motion';
import { AGENTS, AGENTS_ADMISSIONS_EMAIL, AGENTS_CONTACT_NOTE, AGENTS_LEDE } from '@/data/partners';

/** Shared underline-on-hover idiom for navy contact links (email). */
const CONTACT_LINK =
  'font-medium text-brand-navy underline decoration-2 decoration-transparent underline-offset-4 transition-colors duration-300 hover:decoration-brand-gold';

/**
 * Agents (/partners — section 3) — "A local route into IBAS."
 *
 * AGENTS_LEDE frames the network, then the AGENTS render as a
 * ScrollReveal-staggered grid of plain hover-lift cards (a UserCheck chip,
 * the agent name, a MapPin location row, the focus text, and — only where
 * agent.email is published — a mailto link to that official IBAS role
 * alias). Most agents have no email on file and none is invented for them.
 * Below the grid, AGENTS_CONTACT_NOTE pairs with a mailto link to
 * AGENTS_ADMISSIONS_EMAIL for regions without a local representative.
 *
 * Motion: SplitReveal headline, ScrollReveal child-stagger throughout.
 * Reduced-motion safe; every institutional fact comes from
 * '@/data/partners', nothing is invented.
 */
export function Agents() {
  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede ──────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Agents
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            A local route into IBAS.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              {AGENTS_LEDE}
            </p>
          </ScrollReveal>
        </header>

        {/* ── Agents grid ──────────────────────────────────────────────── */}
        <ScrollReveal
          stagger={0.06}
          className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3"
        >
          {AGENTS.map((agent) => (
            <div
              key={agent.name}
              className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <div className="flex size-12 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                <UserCheck aria-hidden className="size-6" />
              </div>
              <h3 className="mt-5 font-display text-xl leading-tight font-semibold text-foreground">
                {agent.name}
              </h3>
              <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                <MapPin aria-hidden className="size-4 shrink-0 text-brand-gold" />
                {agent.location}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{agent.focus}</p>
              {agent.email ? (
                <a
                  href={`mailto:${agent.email}`}
                  className={`mt-4 flex items-center gap-1.5 text-sm break-words ${CONTACT_LINK}`}
                >
                  <Mail aria-hidden className="size-4 shrink-0" />
                  {agent.email}
                </a>
              ) : null}
            </div>
          ))}
        </ScrollReveal>

        {/* ── Fallback note ────────────────────────────────────────────── */}
        <ScrollReveal from="up" delay={0.1}>
          <p className="mt-12 text-sm leading-relaxed text-muted-foreground">
            {AGENTS_CONTACT_NOTE}{' '}
            <a href={`mailto:${AGENTS_ADMISSIONS_EMAIL}`} className={CONTACT_LINK}>
              {AGENTS_ADMISSIONS_EMAIL}
            </a>
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
}
